import { Avatar, ChevronDown } from '@homecare/ui';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Link } from '@tanstack/react-router';
import { Building2, LogOut, ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';

import { useSession } from '../../lib/session';

import { accountItemsFor } from './navigation';

/**
 * Who is signed in, in the top right of the header.
 *
 * ## Why it is not at the bottom of the rail
 *
 * That is where it was, and it is where the reference screenshots put it. It
 * had three problems, and they compounded:
 *
 *   1. **The menu opened upward and covered the navigation.** A menu that hides
 *      the thing you use it to navigate away from is a menu fighting its own
 *      surface. Opening downward from the header covers content instead, which
 *      is redrawn the moment you choose something anyway.
 *   2. **It straddled the rail and the content edge**, so it belonged to
 *      neither — a panel with a navy left half and a white right half is a
 *      seam, not a component.
 *   3. **It repeated the name** in its own header while the trigger two
 *      centimetres below already said it.
 *
 * Top right is also simply where people look. It is the one piece of chrome
 * whose position is near-universal across every product a coordinator uses.
 *
 * ## Three groups, in order of how far they reach
 *
 * **ADMINISTRATION** changes the agency — People, Roles, Settings, Audit log.
 * **YOUR ACCOUNT** changes only you: Two-step sign-in. Then **Sign out**, the
 * one session action, on its own at the bottom.
 *
 * Two-step sign-in used to sit loose among the session actions, which read it
 * as something you *do* now rather than a setting you *hold*. It is a security
 * preference, and the label above it is what says so.
 *
 * No name header: the trigger beside it is showing the name and the role, and a
 * menu that opens by telling you who you are has spent its first row on
 * something you already knew.
 *
 * Administration lives here rather than in the rail because it is not daily
 * work: a scheduler opens the rail forty times a day and never opens any of it.
 *
 * The items are permission-filtered exactly as the rail's are, from the same
 * table. An administrator sees four; a scheduler sees none, and no label and no
 * separator either — an empty group with a heading above it still says "there
 * is something here you cannot have."
 *
 * ## Switch agency is conditional, and that is a safety decision
 *
 * Changing agency means signing in again: an access token carries one
 * `agency_id` (§7), so it genuinely is a new session. For someone who belongs
 * to one agency — which is every staff user we have — that item does nothing
 * except end their work mid-task if they misread it. It renders only when
 * `agencyCount > 1`, and when it does render the consequence is on its own
 * muted line rather than smuggled into the label.
 *
 * The multi-agency model and the login picker are untouched.
 *
 * There is no theme toggle. The product ships one theme; see
 * `lib/single-theme.spec.ts` for the decision and the guard that holds it.
 */

/*
 * `bg-brand-soft`, not `bg-surface-2`.
 *
 * Radix sets `data-highlighted` for the pointer and the keyboard alike, so one
 * rule covers both — but `surface-2` is #F7F9FC against a white menu, which is
 * 1.05:1 and effectively invisible. A highlight nobody can see is the same as
 * no highlight, and it fails a keyboard user worst: they cannot see where they
 * are at all.
 */
const ITEM =
  'text-ink data-[highlighted]:bg-brand-soft data-[highlighted]:text-brand-hover flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm outline-none';

const GROUP_LABEL = 'eyebrow px-3 pb-1 pt-2';

export function UserMenu(): ReactNode {
  const user = useSession((state) => state.user);
  const roles = useSession((state) => state.roles);
  const permissions = useSession((state) => state.permissions);
  const agencyCount = useSession((state) => state.agencyCount);
  const signOut = useSession((state) => state.signOut);

  const administration = accountItemsFor(permissions);
  const canSwitchAgency = agencyCount > 1;

  if (!user) return null;

  const name = `${user.firstName} ${user.lastName}`;
  const role = roles.length > 0 ? roles.join(' · ') : 'No role assigned';

  return (
    <div className="flex items-center gap-1">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="hover:bg-surface-2 duration-fast ease-calm min-h-row-compact flex max-w-56 items-center gap-2.5 rounded-lg py-1 pl-1 pr-2 text-left transition-colors"
          >
            <Avatar name={name} seed={user.id} size="sm" />
            {/* The name and role are hidden on a narrow window rather than
              truncated to initials — a half-name is worse than the avatar,
              which is already carrying the identity. */}
            <span className="hidden min-w-0 sm:block">
              <span className="text-ink block truncate text-sm font-semibold">{name}</span>
              <span className="text-ink-3 block truncate text-xs">{role}</span>
            </span>
            <ChevronDown className="text-ink-3 hidden flex-none sm:block" />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            side="bottom"
            sideOffset={6}
            // `shadow-e4`, not e3. This menu opens over white cards, and a 10%
            // shadow on white against white is a border doing all the work.
            className="border-line bg-surface shadow-e4 w-64 rounded-lg border py-1"
          >
            {administration.length > 0 && (
              <>
                <DropdownMenu.Group>
                  <DropdownMenu.Label className={GROUP_LABEL}>Administration</DropdownMenu.Label>
                  {administration.map((item) => (
                    <DropdownMenu.Item key={item.to} asChild>
                      <Link to={item.to} className={ITEM}>
                        <item.icon aria-hidden="true" className="text-ink-3 h-4 w-4" />
                        {item.label}
                      </Link>
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Group>
                <DropdownMenu.Separator className="bg-line my-1 h-px" />
              </>
            )}

            <DropdownMenu.Group>
              <DropdownMenu.Label className={GROUP_LABEL}>Your account</DropdownMenu.Label>

              <DropdownMenu.Item asChild>
                <Link to="/account/two-step" className={ITEM}>
                  <ShieldCheck aria-hidden="true" className="text-ink-3 h-4 w-4" />
                  Two-step sign-in
                </Link>
              </DropdownMenu.Item>

              {canSwitchAgency && (
                <DropdownMenu.Item onSelect={() => void signOut()} className={ITEM}>
                  <Building2 aria-hidden="true" className="mt-0.5 h-4 w-4 flex-none self-start" />
                  <span className="min-w-0">
                    Switch agency
                    {/*
                    The consequence on its own line rather than inside the
                    label. "Switch agency — signs you out" is a sentence people
                    read the first half of, and the half they skip is the half
                    that ends their session.
                  */}
                    <span className="text-ink-3 block text-xs">Signs you out first</span>
                  </span>
                </DropdownMenu.Item>
              )}
            </DropdownMenu.Group>

            <DropdownMenu.Separator className="bg-line my-1 h-px" />

            <DropdownMenu.Item onSelect={() => void signOut()} className={ITEM}>
              <LogOut aria-hidden="true" className="text-ink-3 h-4 w-4" />
              Sign out
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {/*
        Sign out as a control of its own, not only a row inside the menu.

        An agency runs one or two machines at a desk with coordinators changing
        shift on them. Sign-out two clicks behind a dropdown is how a session
        gets left open on a shared workstation with a client record on screen —
        and the person walking away is the one least likely to spend the second
        click. It stays in the menu as well; this is the copy that has to be
        reachable without thinking.
      */}
      <button
        type="button"
        onClick={() => void signOut()}
        aria-label="Sign out"
        title="Sign out"
        className="text-ink-3 hover:bg-surface-2 hover:text-ink duration-fast ease-calm min-h-web-primary grid w-10 flex-none place-items-center rounded-md transition-colors"
      >
        <LogOut aria-hidden="true" className="h-4 w-4" />
      </button>
    </div>
  );
}
